<template>
  <v-dialog
    v-model="showModal"
    persistent
  >
    <v-card
      class="card-style"
      color="#ECEFF1"
      :style="getBgStyle"
    >
      <br>
      <h2 class="title-style">
        <v-icon class="icon-style">
          mdi-cloud-upload
        </v-icon> Upload Document
      </h2>
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-card-text>
          <v-container fluid>
            <p
              class="my-p-style"
            >
              <font>
                {{ formSubTitle }}
              </font>
            </p>
            <v-autocomplete
              v-model="uploadingDocumentType"
              :items="contractorDocumentTypes"
              item-value="id"
              color="#000000"
              filled
              outlined
              dense
              class="purple-input"
              light
              item-text="name"
              :rules="[(v) => !!v || 'This field is required']"
              label="Select Document Type"
              prepend-icon="mdi-file-document"
              hint="Select your expired document type from here."
              persistent-hint
            />
            <v-file-input
              id="assetsFieldHandle"
              ref="file"
              v-model="documentFile"
              label="Upload Document*"
              :rules="[(v) => !!v || 'This field is required']"
              outlined
              filled
              clearable
              show-size
              color="#000000"
              dense
              light
            />
            <v-dialog
              ref="expiryDialog"
              v-model="expiryCalendar"
              :return-value.sync="documentExpiry"
              width="290px"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="documentExpiry"
                  label="Expiry Date"
                  outlined
                  filled
                  hint="Please select the document expiry date."
                  color="#000000"
                  dense
                  light
                  prepend-icon="mdi-calendar-month-outline"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-if="expiryCalendar"
                v-model="documentExpiry"
                full-width
                color="#37474F"
                light
                :min="minExpiryDate"
              >
                <v-spacer />
                <v-btn
                  text
                  color="#37474F"
                  @click="expiryCalendar = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  text
                  color="#37474F"
                  @click="$refs.expiryDialog.save(documentExpiry)"
                >
                  OK
                </v-btn>
              </v-date-picker>
            </v-dialog>
            <p class="declaration-content">
              I hereby declare that the document uploaded in this process is correct and valid. I will present the same when required.
              <v-checkbox
                v-model="agree"
                light
                class="agree-checkbox"
                dense
                color="#FF3700"
                label="I Agree*"
              />
            </p>
            <v-card-actions>
              <v-spacer />
              <v-btn
                class="mt-2"
                color="#37474F"
                @click="showModal = false"
              >
                Cancel
              </v-btn>
              <v-btn
                class="mt-2"
                color="#FF3700"
                @click="acceptDeclaration()"
              >
                Upload
              </v-btn>
              <v-spacer />
            </v-card-actions>
          </v-container>
        </v-card-text>
      </v-form>
    </v-card>
    <centre-spinner
      :loading="loading"
    />
  </v-dialog>
</template>
<script>
import Constants from 'src/constants';
import spinner from 'src/views/components/SpinnerCentre';
import moment from 'moment';

export default {
  name: 'UploadDocument',
  components: {
    'centre-spinner': spinner,
  },
  props: {
    formSubTitle: {
      type: String,
      required: true,
      default: 'Please upload your document here.',
    },
  },
  data () {
    return {
      showModal: true,
      documentFile: null,
      documentExpiry: '',
      uploadingDocumentType: '',
      expiryCalendar: false,
      loading: false,
      minExpiryDate: moment().format('YYYY-MM-DD'),
      cardBgImg: require('@/assets/img/cardBg.png'),
      valid: false,
      agree: false,
    };
  },
  computed: {
    getBgStyle () {
      return `background-image: linear-gradient(rgba(255,255,255,.6), rgba(255,255,255,.6)), url(${this.cardBgImg});`;
    },
    contractorDocumentTypes () {
      return this.$store.getters['register/getDocumentTypes'];
    },
    userDetails () {
      return this.$store.getters.getUser;
    },
    isDocumentTypesAvailable () {
      if (this.contractorDocumentTypes.length > 0) {
        return true;
      }
      return false;
    },
  },
  watch: {
    showModal: function (newval, oldval) {
      if (newval === false) {
        this.$emit('closed');
      }
    },
  },
  async mounted () {
    this.loading = true;
    await this.fetchContractorDocumentTypes();
    this.loading = false;
  },
  methods: {
    async fetchContractorDocumentTypes () {
      if (this.userDetails.service_provider.contractor_category === null) {
        return;
      }
      const contractorCategory = this.userDetails.service_provider.contractor_category.id;
      await this.$store.dispatch('register/fetchContractorDocumentTypes', {
        categoryId: contractorCategory,
      });
    },
    async acceptDeclaration () {
      if (this.$refs.form.validate() === false || this.agree === false) {
        this.$store.dispatch('alert/onAlert', {
          message: 'Please agree above declaration before proceeding.',
          type: Constants.ALERT_TYPE_ERROR,
        }, { root: true });
        return;
      }
      this.loading = true;
      await this.$store.dispatch('register/acceptDeclaration', {
        declarationDetails: {
          declaration_type: 'documentUpload',
          is_agree: this.agree,
          time: moment.utc().format(),
        },
        externalId: this.userDetails.service_provider.external_id,
      }).then(response => {
        this.uploadDocument();
      }).catch(errors => {
        this.loading = false;
      });
    },
    async uploadDocument () {
      const formData = new FormData();
      formData.append('document_file', this.documentFile);
      formData.append('expiry', this.documentExpiry);
      formData.append('document_type_id', this.uploadingDocumentType);
      await this.$store.dispatch('register/uploadDocumentFiles', {
        documentDetails: formData,
        externalId: this.userDetails.service_provider.external_id,
      }).then(response => {
        this.loading = false;
        this.$store.dispatch('alert/onAlert', {
          message: 'Document uploaded successfully.',
          type: Constants.ALERT_TYPE_SUCCESS,
        });
        this.$emit('upload-success');
      }).catch(() => {
        this.loading = false;
      });
    },
  },
};
</script>
<style scoped>
.title-style {
  color: #37474F;
  margin-left: 30px;
}
.icon-style {
  color: #37474F;
  font-size: 45px;
}
.my-p-style {
  font-size: 16px;
  font-weight: bold;
  color: #546E7A;
  font-family:Cambria, Cochin, Georgia, Times, 'Times New Roman', serif;
  text-align: center;
  padding-bottom: 10px;
}
.card-style {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
.declaration-content {
  font-weight: bold;
  font-size: 12px;
  color: #37474F;
  font-family:'Times New Roman', Times, serif;
}
.agree-checkbox {
  margin-top: 1px;
  margin-left: -5px;
}
</style>
